import React from 'react'
import { WEEKDAY_LABELS_FULL, isSameDay, monthGridDays, addMonths, monthLabel } from './dateUtils'
import './MonthCalendar.css'

// The month grid, shared by the events calendar (`/events/calendar`, month mode) and the
// recruitment home page's upcoming-interviews calendar. It owns the grid, the weekday
// header, the today/outside-month treatment and the "+N till" overflow — and nothing
// domain-specific: what an item is, which day it belongs on, how it renders, and whether a
// day is non-working are all the caller's decisions.
//
// The caller fetches its own data for `monthGridRange(anchor)` (whole weeks, not just the
// month) and owns `anchor` state, so this component stays presentational.
//
// Props:
//   anchor       Date inside the month to display.
//   items        Opaque array handed straight back to itemsForDay/renderItem.
//   itemsForDay  (day, items) => items to show in that cell. Required: an events-style
//                multi-day span and a single-slot interview pick their days differently.
//   renderItem   (item, day) => node. Must set its own key.
//   maxPerDay    Items rendered before collapsing the rest into "+N till" (default 3).
//   dayClassName (day) => extra cell classes, e.g. 'non-working' / 'holiday'.
//   dayTitle     (day) => title attribute for the cell.
export const MonthCalendar = ({
  anchor,
  items = [],
  itemsForDay,
  renderItem,
  maxPerDay = 3,
  dayClassName,
  dayTitle,
}) => {
  const days = monthGridDays(anchor)
  const today = new Date()

  return (
    <div className="month-cal-scroll">
      <div className="month-cal-weekday-row">
        {WEEKDAY_LABELS_FULL.map(l => <div className="month-cal-weekday" key={l}>{l}</div>)}
      </div>
      <div className="month-cal-grid">
        {days.map(day => {
          const dayItems = itemsForDay(day, items)
          const visible  = dayItems.slice(0, maxPerDay)
          const overflow = dayItems.length - visible.length
          const extraClass = dayClassName ? dayClassName(day) : ''
          const title = dayTitle ? dayTitle(day) : undefined
          return (
            <div
              className={"month-cal-cell"
                + (day.getMonth() === anchor.getMonth() ? "" : " outside")
                + (isSameDay(day, today) ? " today" : "")
                + (extraClass ? " " + extraClass : "")}
              title={title || undefined}
              key={day.toISOString()}
            >
              <div className="month-cal-daynum">{day.getDate()}</div>
              <div className="month-cal-cell-body">
                {visible.map(item => renderItem(item, day))}
                {overflow > 0 && <div className="month-cal-more">+{overflow} till</div>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Month stepper for a page whose only calendar is this one. The events calendar has its
// own nav instead, because there it has to step by week or by month depending on the
// active mode — this is the plain month-only case.
export const MonthCalendarNav = ({ anchor, onAnchorChange, children }) => (
  <div className="month-cal-nav">
    <button type="button" className="month-cal-nav-btn" onClick={() => onAnchorChange(addMonths(anchor, -1))} aria-label="Föregående månad">‹</button>
    <div className="month-cal-nav-label">{monthLabel(anchor)}</div>
    <button type="button" className="month-cal-nav-btn" onClick={() => onAnchorChange(addMonths(anchor, 1))} aria-label="Nästa månad">›</button>
    <button type="button" className="month-cal-nav-today" onClick={() => onAnchorChange(new Date())}>Idag</button>
    {children}
  </div>
)

export default MonthCalendar
